import {
  Button,
  Checkbox,
  DialogFooter,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@design-system';
import {
  FINANCIAL_ASSET_TYPES_PREFILL_OPTIONS,
  GENDER_AT_BIRTH_PREFILL_OPTIONS,
  HIGHEST_DEGREE_PREFILL_OPTIONS,
  LEGAL_DOCUMENTS_PREFILL_OPTIONS,
  MARITAL_STATUS_PREFILL_OPTIONS,
  PREFILL_FIELD_RULES,
  PREFILL_QUESTION_LABELS,
  PROFESSIONAL_SUPPORT_NETWORK_PREFILL_OPTIONS,
  isAdvisorPrefillQuestionAllowed,
  shouldAskPartnerQuestions,
} from '@shared';
import type { AdvisorPrefillPolicy } from '@shared';
import {
  usePrefillDialog,
  type PrefillDraft,
  type PrefillMultiSelectField,
} from '../hooks/use-prefill-dialog';
import type { PrefillValidationErrors } from '../hooks/validate-prefill-draft';
import { PrefillDateField } from './prefill-date-field';
import { PrefillPlanRequiredLabel } from './prefill-plan-required-label';

import { CheckCircle2, ChevronLeft, ClipboardList, Pencil } from 'lucide-react';

import type { ReactNode } from 'react';

interface PrefillSectionProps {
  title: string;
  children: ReactNode;
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p
      role="alert"
      className="text-destructive text-sm"
    >
      {message}
    </p>
  );
}

function PrefillSection({ title, children }: PrefillSectionProps) {
  return (
    <div className="space-y-4">
      <h5 className="text-sm font-semibold text-gray-950">{title}</h5>
      {children}
    </div>
  );
}

interface PrefillSelectProps {
  label: string;
  placeholder: string;
  value: string;
  options: readonly string[];
  onValueChange: (value: string) => void;
  planRequired?: boolean;
}

function PrefillSelect({
  label,
  placeholder,
  value,
  options,
  onValueChange,
  planRequired,
}: PrefillSelectProps) {
  return (
    <div className="flex flex-col gap-2">
      {planRequired ? (
        <PrefillPlanRequiredLabel>{label}</PrefillPlanRequiredLabel>
      ) : (
        <Label>{label}</Label>
      )}
      <Select
        value={value}
        onValueChange={onValueChange}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map(option => (
            <SelectItem
              key={option}
              value={option}
            >
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

interface PrefillCurrencyFieldProps {
  id: string;
  label: string;
  placeholder: string;
  value: string;
  error?: string;
  onChange: (value: string) => void;
}

function PrefillCurrencyField({
  id,
  label,
  placeholder,
  value,
  error,
  onChange,
}: PrefillCurrencyFieldProps) {
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <span className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm">
          $
        </span>
        <Input
          id={id}
          type="text"
          inputMode="numeric"
          className="pl-7"
          placeholder={placeholder}
          aria-invalid={!!error}
          value={value}
          onChange={({ target }) => onChange(target.value)}
        />
      </div>
      <FieldError message={error} />
    </div>
  );
}

interface PrefillCheckboxGroupProps {
  label: string;
  field: PrefillMultiSelectField;
  options: readonly string[];
  selected: string[];
  onToggle: (field: PrefillMultiSelectField, choice: string, checked: boolean) => void;
}

function PrefillCheckboxGroup({
  label,
  field,
  options,
  selected,
  onToggle,
}: PrefillCheckboxGroupProps) {
  return (
    <div className="flex flex-col gap-2">
      <Label>{label}</Label>
      <div className="space-y-2 rounded-lg border p-3">
        {options.map((option, index) => {
          const optionId = `prefill-${field}-${index}`;

          return (
            <div
              key={option}
              className="flex cursor-pointer items-start gap-3"
            >
              <Checkbox
                id={optionId}
                checked={selected.includes(option)}
                onCheckedChange={checked => onToggle(field, option, !!checked)}
                className="translate-y-0.5"
              />
              <Label
                htmlFor={optionId}
                className="text-foreground cursor-pointer text-sm font-normal"
              >
                {option}
              </Label>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface PrefillFieldsProps {
  policy: AdvisorPrefillPolicy;
  draft: PrefillDraft;
  errors: PrefillValidationErrors;
  birthDateInput: string;
  onBirthDateInputChange: (value: string) => void;
  onBirthDateInputValidityChange: (isValid: boolean) => void;
  onDraftChange: <Field extends keyof PrefillDraft>(
    field: Field,
    value: PrefillDraft[Field],
  ) => void;
  onToggleChoice: (field: PrefillMultiSelectField, choice: string, checked: boolean) => void;
}

function PrefillFields({
  policy,
  draft,
  errors,
  birthDateInput,
  onBirthDateInputChange,
  onBirthDateInputValidityChange,
  onDraftChange,
  onToggleChoice,
}: PrefillFieldsProps) {
  const allows = (questionId: string) => isAdvisorPrefillQuestionAllowed(policy, questionId);
  const showPartnerQuestions =
    allows('partnerFirstName') &&
    allows('partnerLastName') &&
    (!allows('clientMaritalStatus') || shouldAskPartnerQuestions(draft.maritalStatus));

  return (
    <div className="space-y-6">
      <PrefillSection title="About your client">
        <div className="flex flex-col gap-4">
          <PrefillDateField
            id="prefill-birth-date"
            label={PREFILL_QUESTION_LABELS.clientBirthDate}
            planRequired={policy.canCreateQuickPlan}
            value={draft.birthDate}
            inputValue={birthDateInput}
            error={errors.birthDate}
            onChange={date => onDraftChange('birthDate', date)}
            onInputValueChange={onBirthDateInputChange}
            onInputValidityChange={onBirthDateInputValidityChange}
          />
          <div className="flex flex-col gap-2">
            {policy.canCreateQuickPlan ? (
              <PrefillPlanRequiredLabel htmlFor="prefill-zip-code">
                {PREFILL_QUESTION_LABELS.clientZipCode}
              </PrefillPlanRequiredLabel>
            ) : (
              <Label htmlFor="prefill-zip-code">{PREFILL_QUESTION_LABELS.clientZipCode}</Label>
            )}
            <Input
              id="prefill-zip-code"
              type="text"
              inputMode="numeric"
              maxLength={PREFILL_FIELD_RULES.clientZipCode.maxLength}
              placeholder="94105"
              aria-invalid={!!errors.zipCode}
              value={draft.zipCode}
              onChange={({ target }) => onDraftChange('zipCode', target.value)}
            />
            <FieldError message={errors.zipCode} />
          </div>
          {allows('clientGenderAtBirth') && (
            <PrefillSelect
              label={PREFILL_QUESTION_LABELS.clientGenderAtBirth}
              planRequired={policy.canCreateQuickPlan}
              placeholder="Select sex at birth"
              value={draft.genderAtBirth}
              options={GENDER_AT_BIRTH_PREFILL_OPTIONS}
              onValueChange={value => onDraftChange('genderAtBirth', value)}
            />
          )}
          {allows('clientMaritalStatus') && (
            <PrefillSelect
              label={PREFILL_QUESTION_LABELS.clientMaritalStatus}
              placeholder="Select marital status"
              value={draft.maritalStatus}
              options={MARITAL_STATUS_PREFILL_OPTIONS}
              onValueChange={value => onDraftChange('maritalStatus', value)}
            />
          )}
          {showPartnerQuestions && (
            <fieldset className="flex flex-col gap-2">
              <legend className="mb-2 text-sm font-medium leading-none">
                What is your partner's name?
              </legend>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="prefill-partner-first-name">First name</Label>
                  <Input
                    id="prefill-partner-first-name"
                    type="text"
                    maxLength={PREFILL_FIELD_RULES.partnerFirstName.maxLength}
                    aria-invalid={!!errors.partnerFirstName}
                    value={draft.partnerFirstName}
                    onChange={({ target }) => onDraftChange('partnerFirstName', target.value)}
                  />
                  <FieldError message={errors.partnerFirstName} />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="prefill-partner-last-name">Last name</Label>
                  <Input
                    id="prefill-partner-last-name"
                    type="text"
                    maxLength={PREFILL_FIELD_RULES.partnerLastName.maxLength}
                    aria-invalid={!!errors.partnerLastName}
                    value={draft.partnerLastName}
                    onChange={({ target }) => onDraftChange('partnerLastName', target.value)}
                  />
                  <FieldError message={errors.partnerLastName} />
                </div>
              </div>
            </fieldset>
          )}
        </div>
      </PrefillSection>

      <PrefillSection title="Household & finances">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="prefill-household-size">
              {PREFILL_QUESTION_LABELS.householdHouseholdSize}
            </Label>
            <Input
              id="prefill-household-size"
              type="number"
              min={PREFILL_FIELD_RULES.householdHouseholdSize.min}
              max={PREFILL_FIELD_RULES.householdHouseholdSize.max}
              placeholder="2"
              aria-invalid={!!errors.householdSize}
              value={draft.householdSize}
              onChange={({ target }) => onDraftChange('householdSize', target.value)}
            />
            <FieldError message={errors.householdSize} />
          </div>
          <PrefillCurrencyField
            id="prefill-total-assets"
            label={PREFILL_QUESTION_LABELS.householdTotalAssetsValue}
            placeholder="500000"
            value={draft.totalAssets}
            error={errors.totalAssets}
            onChange={value => onDraftChange('totalAssets', value)}
          />
          <PrefillCurrencyField
            id="prefill-prior-year-income"
            label={PREFILL_QUESTION_LABELS.householdPriorYearIncome}
            placeholder="120000"
            value={draft.priorYearIncome}
            error={errors.priorYearIncome}
            onChange={value => onDraftChange('priorYearIncome', value)}
          />
        </div>
        <PrefillCheckboxGroup
          label={PREFILL_QUESTION_LABELS.financialAssetTypes}
          field="assetTypes"
          options={FINANCIAL_ASSET_TYPES_PREFILL_OPTIONS}
          selected={draft.assetTypes}
          onToggle={onToggleChoice}
        />
      </PrefillSection>

      <PrefillSection title="Planning & support">
        <PrefillCheckboxGroup
          label={PREFILL_QUESTION_LABELS.legalDocuments}
          field="legalDocuments"
          options={LEGAL_DOCUMENTS_PREFILL_OPTIONS}
          selected={draft.legalDocuments}
          onToggle={onToggleChoice}
        />
        {allows('clientProfessionalSupportNetworkList') && (
          <PrefillCheckboxGroup
            label={PREFILL_QUESTION_LABELS.clientProfessionalSupportNetworkList}
            field="supportNetwork"
            options={PROFESSIONAL_SUPPORT_NETWORK_PREFILL_OPTIONS}
            selected={draft.supportNetwork}
            onToggle={onToggleChoice}
          />
        )}
        {allows('clientHighestDegree') && (
          <PrefillSelect
            label={PREFILL_QUESTION_LABELS.clientHighestDegree}
            placeholder="Select education level"
            value={draft.highestDegree}
            options={HIGHEST_DEGREE_PREFILL_OPTIONS}
            onValueChange={value => onDraftChange('highestDegree', value)}
          />
        )}
      </PrefillSection>
    </div>
  );
}

export function PrefillAnswersSection() {
  const {
    isOpen,
    draft,
    errors,
    hasErrors,
    birthDateInput,
    prefilledCount,
    isDraftPlanReady,
    draftMissingPlanFieldsLabel,
    isSavedPlanReady,
    openDialog,
    closeDialog,
    cancelDialog,
    saveDialog,
    clearDraft,
    setBirthDateInput,
    setBirthDateInputValidity,
    handleDraftChange,
    toggleDraftChoice,
    advisorPrefillPolicy,
  } = usePrefillDialog();

  const savedSummaryTitle =
    prefilledCount === 0
      ? 'Pre-fill answers for your client'
      : `${prefilledCount} answer${prefilledCount === 1 ? '' : 's'} pre-filled`;
  const savedSummaryDescription =
    prefilledCount === 0 && advisorPrefillPolicy.canCreateQuickPlan
      ? 'Add their birth date, sex at birth and zip code, and their plan is ready before they fill anything in.'
      : "Answers you provide are pre-filled in your client's intake form. Your client can review and change them.";
  const showSavedSuccess =
    prefilledCount > 0 && (!advisorPrefillPolicy.canCreateQuickPlan || isSavedPlanReady);

  if (!isOpen) {
    return (
      <Button
        type="button"
        variant="outline"
        className={`h-auto w-full justify-start gap-3 whitespace-normal px-4 py-3 text-left ${
          showSavedSuccess ? 'border-green-600/35 bg-green-600/5' : 'bg-muted'
        }`}
        onClick={openDialog}
      >
        <span
          className={
            showSavedSuccess
              ? 'flex size-8 shrink-0 items-center justify-center rounded-lg bg-green-600/15 text-green-700'
              : 'bg-primary/10 text-primary flex size-8 shrink-0 items-center justify-center rounded-lg'
          }
        >
          {isSavedPlanReady && prefilledCount > 0 ? (
            <CheckCircle2 className="size-4" />
          ) : (
            <ClipboardList className="size-4" />
          )}
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-sm font-semibold text-gray-950">{savedSummaryTitle}</span>
          <span className="text-muted-foreground text-xs font-normal">
            {savedSummaryDescription}
          </span>
        </span>
        <span className="bg-background text-foreground flex shrink-0 items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium">
          {prefilledCount > 0 && <Pencil className="size-3.5" />}
          {prefilledCount > 0 ? 'Edit' : 'Add answers'}
        </span>
      </Button>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="shrink-0 space-y-2">
        <Button
          type="button"
          variant="link"
          className="h-auto w-fit gap-1 px-0 py-0 text-xs"
          onClick={closeDialog}
        >
          <ChevronLeft className="size-3.5" />
          Back to invite
        </Button>
        <h2 className="text-lg font-semibold text-gray-950">Pre-fill answers</h2>
        <p className="text-foreground pr-7 text-sm">
          Everything here is optional and your client can change any of it. Nothing is sent to them
          yet.
        </p>
      </div>

      <div className="-mx-6 min-h-0 flex-1 overflow-y-auto px-6 pb-2">
        <PrefillFields
          policy={advisorPrefillPolicy}
          draft={draft}
          errors={errors}
          birthDateInput={birthDateInput}
          onBirthDateInputChange={setBirthDateInput}
          onBirthDateInputValidityChange={setBirthDateInputValidity}
          onDraftChange={handleDraftChange}
          onToggleChoice={toggleDraftChoice}
        />
      </div>

      {advisorPrefillPolicy.canCreateQuickPlan && !isDraftPlanReady && (
        <p
          aria-live="polite"
          className="text-muted-foreground shrink-0 pt-3 text-xs"
        >
          Without {draftMissingPlanFieldsLabel}, your client has no plan until they finish the form
          themselves.
        </p>
      )}

      <DialogFooter className="bg-background shrink-0 border-t pt-4">
        <Button
          type="button"
          variant="ghost"
          className="sm:mr-auto"
          onClick={clearDraft}
        >
          Clear all
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={cancelDialog}
        >
          Cancel
        </Button>
        <Button
          type="button"
          disabled={hasErrors}
          onClick={saveDialog}
        >
          Save answers
        </Button>
      </DialogFooter>
    </div>
  );
}
